import { Trans, useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import cx from 'clsx';
import Tooltip from 'antd/es/tooltip';

import viewStyle from 'css/shared/view.module.css';
import { IconRefresh } from 'assets/overview';
import { useDeviceTypeIsMobile } from 'hooks';
import { formatTimeReadable } from 'utils';

import style from './index.module.css';

interface UpdateButtonProps {
  onUpdateClick(): void;
  updating?: boolean;
  fromTime?: number;
  disabled?: boolean;
  disabledTip?: string;
  hoverTip?: string;
  hideFromTime?: boolean;
  iconSize?: number;
}

export const UpdateButton = memo(
  ({
    onUpdateClick,
    updating = false,
    fromTime = new Date().getTime(),
    disabled,
    disabledTip,
    hoverTip,
    hideFromTime,
    iconSize = 14,
  }: UpdateButtonProps) => {
    const { t } = useTranslation();

    const [elapseSecs, setElpaseSecs] = useState(Math.ceil(+new Date() / 1000 - fromTime));
    const isMobile = useDeviceTypeIsMobile();
    const timer = useRef(-1);
    const [updatingStyle, setUpdatingStyle] = useState(updating);

    const handleRefreshClick = useCallback(() => {
      if (updating || disabled || !!disabledTip) {
        return;
      }

      onUpdateClick();
    }, [onUpdateClick, updating, disabled, disabledTip]);

    const forwardSeconds = useCallback(() => {
      timer.current = window.setTimeout(() => {
        setElpaseSecs(Math.ceil(+new Date() / 1000 - fromTime));
        // 这会肯定是 false，1s 后再停
        setUpdatingStyle(updating);
        forwardSeconds();
      }, 1000);
    }, [fromTime, updating, setUpdatingStyle]);

    useEffect(() => {
      const clearTimer = () => {
        if (timer.current !== -1) {
          clearTimeout(timer.current);
          timer.current = -1;
        }
      };

      if (updating) {
        clearTimer();
        setElpaseSecs(0);
        // 只要有更新，就先转起来
        setUpdatingStyle(true);
        return;
      }

      forwardSeconds();

      return clearTimer;
    }, [setElpaseSecs, updating, forwardSeconds]);

    const timeText = useMemo(
      () => (Number.isFinite(elapseSecs) ? formatTimeReadable(elapseSecs) : ''),
      [elapseSecs],
    );

    return (
      <Tooltip title={disabledTip || null}>
        <span
          className={cx(
            isMobile ? style.mRefresh : style.refresh,
            isMobile && viewStyle.font11,
            updating && style.fetch,
            (!!disabledTip || disabled) && style.disabled,
          )}>
          {hideFromTime ? null : updating ? (
            <>{t('UpdateButton.zcg1lw')}</>
          ) : elapseSecs && timeText ? (
            <Trans i18nKey="UpdateButton.inhied" values={{ time: timeText }}>
              Data updated
              <span className={style.updateTimeNumber}>{timeText}</span>
              ago
            </Trans>
          ) : null}
          <Tooltip title={disabledTip ? null : hoverTip}>
            <IconRefresh
              className={cx(style.refreshIcon, updatingStyle && style.isUpdating)}
              onClick={handleRefreshClick}
              style={{ width: iconSize, height: iconSize }}
            />
          </Tooltip>
        </span>
      </Tooltip>
    );
  },
);

export default UpdateButton;
